/* eslint-disable camelcase */
import React from 'react'
import { css, StyleSheet } from 'aphrodite'
import { Tooltip } from '@material-ui/core'

import { ApiHelper } from '../../../common/helpers/ApiHelper'
import { Helper } from '../../../common/helpers/Helper'
import { UiHelper } from '../../../common/helpers/UiHelper'
import { BaseEntityPageSlixta } from '../../../components/BaseEntityPageSlixta'
import { AppConfig } from '../../../AppConfig'
import { AppStyles } from '../../../styles/AppTheme'
import { CommonTheme } from '../../../styles/Styles'
import { SearchBar } from '../../../common/components/SearchBar'
import { Controller } from '../../../common/Controller'
import { AppHelper } from '../../../helpers/AppHelper'

import {
  isMainOrgTeam,
  formatInboundRoutingSummary,
  formatOutboundSummary,
  getTeamLocationAddress,
  extractCustomFieldValues,
  isValidTeamTitle,
  getTeamTitleError,
  buildCreateTeamFormDef,
  buildEditTeamFormDef,
  buildRoutingFormDef,
  buildOutboundFormDef,
  compareTeamsByCreatedTs,
} from './TeamsHelper'
import { TeamStaffDrawer } from './TeamStaffDrawer'

export class Teams extends BaseEntityPageSlixta {
  constructor(props) {
    super(props)

    this.ms = AppConfig.CDP_MS.ENDPOINT

    const rawOrgId =
      (props.match && props.match.params && props.match.params.orgId) ||
      AppHelper.parseOrgId(window.location.pathname) ||
      (Controller.get().roleHelper() && Controller.get().roleHelper().getOrgId()) ||
      'PRODUCT'

    const { orgId: parsedParentId } = AppHelper.parseOrgUnitId(rawOrgId)
    this.parentOrgId = parsedParentId || rawOrgId
    this.orgId = this.parentOrgId

    this.pageTitle = Helper.getString('teams', 'Teams')
    this.pageKey = 'teams'
    this.noAdd = true
    this.emptyText = 'No teams configured yet'

    this.breadCrumbs = [
      { title: Helper.getString('home'), to: this.baseRoute() },
      { title: this.pageTitle },
    ]

    this.toFetch = [
      {
        key: 'teams',
        ms: this.ms,
        method: 'GET',
        paths: ['miscs', 'get-teams'],
        queryParams: {
          // orgId required for RBAC; parentOrgId required by get-teams handler
          orgId: this.parentOrgId,
          parentOrgId: this.parentOrgId,
          pb: 'fieldDef,fieldDefRouting,fieldDefOutbound',
        },
      },
    ]

    if (this.parentOrgId && this.parentOrgId !== 'PRODUCT') {
      this.toFetch.push({
        key: 'mainorg',
        method: 'GET',
        paths: ['orgs', this.parentOrgId],
      })
    }

    this.apiData = {
      orgId: this.orgId,
    }

    this.updateHeaderActions()

    this.tableHeadersMap = {
      Name: current => this.renderNameCell(current),
      'Inbound Routing': current => this.renderInboundRoutingCell(current),
      'Outbound Channels': current => this.renderOutboundCell(current),
    }

    this.tableHeaders = Object.keys(this.tableHeadersMap)

    this.cellWidths = {
      Name: Styles.cellWidthName,
      'Inbound Routing': Styles.cellWidthInbound,
      'Outbound Channels': Styles.cellWidthOutbound,
      actions: Styles.cellWidthActions,
    }

    this.state = {
      ...this.state,
      masterTeamList: [],
      parentOrgTitle: '',
      fieldDef: null,
      fieldDefRouting: null,
      fieldDefOutbound: null,
      searchText: '',

      staffDrawerOpen: false,
      selectedTeam: null,
    }
  }

  resolveParentOrgTitle(mainOrgResult) {
    if (mainOrgResult && mainOrgResult.title && mainOrgResult.title !== 'Main Org') {
      return mainOrgResult.title
    }
    const roleHelper = Controller.get().roleHelper()
    if (roleHelper) {
      const orgs = roleHelper.getOrgs() || []
      const matched =
        orgs.find(o => (o.orgId === this.parentOrgId || o.id === this.parentOrgId) && !o.subOrgId) ||
        orgs.find(o => o.orgId === this.parentOrgId || o.id === this.parentOrgId)
      if (matched && matched.title && matched.title !== 'Main Org') {
        return matched.title
      }
      const mainTitle = roleHelper.getMainOrgTitle()
      if (mainTitle && mainTitle !== 'Main Org' && mainTitle !== this.parentOrgId) {
        return mainTitle
      }
      const unitTitle = roleHelper.getOrgUnitTitle()
      if (unitTitle && unitTitle !== 'Main Org' && unitTitle !== this.parentOrgId) {
        return unitTitle
      }
    }
    return UiHelper.getMainOrgTitle() || UiHelper.getOrgTitle() || ''
  }

  async onFetchSuccess(results) {
    const teamsResult = this.getResult(results, 'teams') || results[0]
    const rawItems = Array.isArray(teamsResult && teamsResult.items)
      ? teamsResult.items
      : Helper.dictToArray(teamsResult && teamsResult.items)

    const mainOrgResult = this.getResult(results, 'mainorg')
    let parentOrgTitle = this.resolveParentOrgTitle(mainOrgResult)

    if (!parentOrgTitle && this.parentOrgId && this.parentOrgId !== 'PRODUCT') {
      try {
        const orgData = await ApiHelper.callAwait({
          method: 'GET',
          paths: ['orgs', this.parentOrgId],
        })
        if (orgData && orgData.title && orgData.title !== 'Main Org') {
          parentOrgTitle = orgData.title
        }
      } catch (err) {
        // ignore fallback
      }
    }

    const sortedItems = rawItems
      .filter(team => !isMainOrgTeam(team, this.parentOrgId))
      .sort(compareTeamsByCreatedTs)

    const filtered = this.filterTeams(sortedItems, this.state.searchText)

    this.updateHeaderActions()

    this.setState({
      parentOrgTitle,
      masterTeamList: sortedItems,
      items: filtered,
      fieldDef: (teamsResult && teamsResult.fieldDef) || null,
      fieldDefRouting: (teamsResult && teamsResult.fieldDefRouting) || null,
      fieldDefOutbound: (teamsResult && teamsResult.fieldDefOutbound) || null,
    })
  }

  updateHeaderActions = () => {
    const actions = []
    if (AppHelper.isTeamsEnabled(this.parentOrgId)) {
      actions.push({
        label: 'New Team',
        onClick: () => this.openCreateDialog(),
      })
    }
    actions.push({
      label: 'Refresh',
      tooltip: 'Refresh',
      icon: 'refresh',
      type: 'icon-label',
      onClick: () => this.fetchItems(),
    })
    this.headerActions = actions
  }

  async componentDidMount() {
    super.componentDidMount()
    if (this.parentOrgId && this.parentOrgId !== 'PRODUCT') {
      try {
        await Controller.get().cdpHelper().loadSettings(this.parentOrgId)
        this.updateHeaderActions()
        this.forceUpdate()
      } catch (err) {
        // ignore
      }
    }
  }

  renderHeaderbar() {
    this.updateHeaderActions()
    return super.renderHeaderbar()
  }

  filterTeams = (masterList, searchText) => {
    let rows = masterList || []
    const q = (searchText || '').trim().toLowerCase()
    if (q) {
      rows = rows.filter(team => {
        const titleMatch = (team.title || '').toLowerCase().includes(q)
        const idMatch = (team.id || '').toLowerCase().includes(q)
        const address = (getTeamLocationAddress(team) || '').toLowerCase()
        const addressMatch = address.includes(q)
        const slugsMatch =
          Array.isArray(team.inboundPageSlugs) &&
          team.inboundPageSlugs.some(s => s.toLowerCase().includes(q))
        return titleMatch || idMatch || addressMatch || slugsMatch
      })
    }
    return rows
  }

  onSearch = (searchText = '') => {
    const items = this.filterTeams(this.state.masterTeamList, searchText)
    this.setState({ searchText, items })
  }

  clearSearch = () => {
    const items = this.filterTeams(this.state.masterTeamList, '')
    this.setState({ searchText: '', items })
  }

  onFormFieldOptionAdd = async ({ formField, value }) => {
    if (!formField.options) {
      formField.options = []
    }
    const trimmed = String(value || '').trim()
    if (!trimmed) return formField.options
    const existing = formField.options.find(
      o => o.key === trimmed || o.label === trimmed || o.text === trimmed
    )
    if (!existing) {
      const newOpt = {
        key: trimmed,
        label: trimmed,
        text: trimmed,
      }
      formField.options.push(newOpt)
    }
    return formField.options
  }

  openCreateDialog = () => {
    if (!AppHelper.isTeamsEnabled(this.parentOrgId)) {
      this.setState({
        showToast: true,
        toastMsg: 'Team creation is disabled because Multi-Location (Teams) is not enabled.',
        isErrorToast: true,
      })
      return
    }
    const formDef = buildCreateTeamFormDef()
    this.formDefinition = formDef
    this.openModalForAdd(formDef)
  }

  openEditDialog = (team) => {
    const isMain = isMainOrgTeam(team, this.parentOrgId)
    const effectiveTitle = isMain
      ? (this.state.parentOrgTitle || (team.title && team.title !== 'Main Org' ? team.title : UiHelper.getMainOrgTitle()) || team.title || 'Main Org')
      : (team.title || '')
    const teamWithTitle = { ...team, title: effectiveTitle }

    const formDef = buildEditTeamFormDef({
      team: teamWithTitle,
      isMain,
      fieldDef: this.state.fieldDef,
    })
    this.formDefinition = formDef

    const prefill = {
      title: effectiveTitle,
      ...(team.customFieldValues || {}),
      ...team,
      locationAddress: getTeamLocationAddress(team) || team.locationAddress || '',
    }
    this.openModalForEdit(prefill, formDef)
  }

  openRoutingDialog = (team) => {
    const formDef = buildRoutingFormDef({
      team,
      fieldDefRouting: this.state.fieldDefRouting,
    })
    this.formDefinition = formDef

    const prefill = {
      inboundPageSlugs: team.inboundPageSlugs || [],
      inboundMetaFormIds: team.inboundMetaFormIds || [],
      inboundCallNumbers: team.inboundCallNumbers || [],
      inboundWANumbers: team.inboundWANumbers || [],
    }
    this.openModalForEdit(prefill, formDef)
  }

  openOutboundDialog = (team) => {
    const formDef = buildOutboundFormDef({
      team,
      fieldDefOutbound: this.state.fieldDefOutbound,
    })
    this.formDefinition = formDef

    const prefill = {
      outgoingWaNumberId: team.outgoingWaNumberId || '',
      outgoingCtcNumberId: team.outgoingCtcNumberId || '',
    }
    this.openModalForEdit(prefill, formDef)
  }

  openStaffDrawer = (team) => {
    this.setState({
      selectedTeam: team,
      staffDrawerOpen: true,
    })
  }

  closeStaffDrawer = () => {
    this.setState({
      staffDrawerOpen: false,
      selectedTeam: null,
    })
  }

  customSubmit = async (options, done) => {
    const { formData, caller } = options
    const dialogType = this.formDefinition && this.formDefinition._dialogType

    if (dialogType === 'create') {
      if (!AppHelper.isTeamsEnabled(this.parentOrgId)) {
        caller.setState({
          success: false,
          message: 'Team creation is disabled because Multi-Location (Teams) is not enabled.',
          inProcess: false,
        })
        return
      }
      const title = (formData.title || '').trim()
      if (!isValidTeamTitle(title)) {
        caller.setState({
          success: false,
          message: getTeamTitleError(title),
          inProcess: false,
        })
        return
      }

      const payload = {
        parentOrgId: this.parentOrgId,
        title,
      }

      try {
        const response = await ApiHelper.callAwait({
          ms: this.ms,
          method: 'POST',
          paths: ['miscs', 'create-team'],
          queryParams: { orgId: this.parentOrgId },
          jsonBody: payload,
        })

        if (response && response.err) {
          caller.setState({
            success: false,
            message: Helper.getErrorMsg(response.err),
            inProcess: false,
          })
          return
        }

        this.setState({
          modalOpen: false,
          showToast: true,
          toastMsg: 'Team created successfully',
          isErrorToast: false,
        })
        this.fetchItems()
        if (done) done()
      } catch (err) {
        caller.setState({
          success: false,
          message: Helper.getErrorMsg(err),
          inProcess: false,
        })
      }
    } else if (dialogType === 'edit') {
      const team = this.formDefinition._selectedTeam
      const isMain = isMainOrgTeam(team, this.parentOrgId)

      const payload = {
        parentOrgId: this.parentOrgId,
        teamId: team.id,
        customFieldValues: extractCustomFieldValues(formData, this.state.fieldDef),
      }
      if (formData.locationAddress !== undefined) {
        payload.locationAddress = formData.locationAddress
      }

      if (!isMain) {
        const title = (formData.title || '').trim()
        if (!isValidTeamTitle(title)) {
          caller.setState({
            success: false,
            message: getTeamTitleError(title),
            inProcess: false,
          })
          return
        }
        payload.title = title
      }

      try {
        const response = await ApiHelper.callAwait({
          ms: this.ms,
          method: 'POST',
          paths: ['miscs', 'update-team'],
          queryParams: { orgId: this.parentOrgId },
          jsonBody: payload,
        })

        if (response && response.err) {
          caller.setState({
            success: false,
            message: Helper.getErrorMsg(response.err),
            inProcess: false,
          })
          return
        }

        this.setState({
          modalOpen: false,
          showToast: true,
          toastMsg: 'Team details updated successfully',
          isErrorToast: false,
        })
        this.fetchItems()
        if (done) done()
      } catch (err) {
        caller.setState({
          success: false,
          message: Helper.getErrorMsg(err),
          inProcess: false,
        })
      }
    } else if (dialogType === 'routing') {
      const team = this.formDefinition._selectedTeam
      const payload = {
        parentOrgId: this.parentOrgId,
        teamId: team.id,
        inboundPageSlugs: formData.inboundPageSlugs || [],
        inboundMetaFormIds: formData.inboundMetaFormIds || [],
        inboundCallNumbers: formData.inboundCallNumbers || [],
        inboundWANumbers: formData.inboundWANumbers || [],
      }

      try {
        const response = await ApiHelper.callAwait({
          ms: this.ms,
          method: 'POST',
          paths: ['miscs', 'update-team'],
          queryParams: { orgId: this.parentOrgId },
          jsonBody: payload,
        })

        if (response && response.err) {
          caller.setState({
            success: false,
            message: Helper.getErrorMsg(response.err),
            inProcess: false,
          })
          return
        }

        this.setState({
          modalOpen: false,
          showToast: true,
          toastMsg: 'Inbound routing updated successfully',
          isErrorToast: false,
        })
        this.fetchItems()
        if (done) done()
      } catch (err) {
        caller.setState({
          success: false,
          message: Helper.getErrorMsg(err),
          inProcess: false,
        })
      }
    } else if (dialogType === 'outbound') {
      const team = this.formDefinition._selectedTeam
      const payload = {
        parentOrgId: this.parentOrgId,
        teamId: team.id,
        outgoingWaNumberId: formData.outgoingWaNumberId || '',
        outgoingCtcNumberId: formData.outgoingCtcNumberId || '',
      }

      try {
        const response = await ApiHelper.callAwait({
          ms: this.ms,
          method: 'POST',
          paths: ['miscs', 'update-team'],
          queryParams: { orgId: this.parentOrgId },
          jsonBody: payload,
        })

        if (response && response.err) {
          caller.setState({
            success: false,
            message: Helper.getErrorMsg(response.err),
            inProcess: false,
          })
          return
        }

        this.setState({
          modalOpen: false,
          showToast: true,
          toastMsg: 'Outbound channels updated successfully',
          isErrorToast: false,
        })
        this.fetchItems()
        if (done) done()
      } catch (err) {
        caller.setState({
          success: false,
          message: Helper.getErrorMsg(err),
          inProcess: false,
        })
      }
    }
  }

  renderNameCell(current) {
    const isMain = isMainOrgTeam(current, this.parentOrgId)
    const displayName = isMain
      ? (this.state.parentOrgTitle || (current.title && current.title !== 'Main Org' ? current.title : UiHelper.getMainOrgTitle()) || current.title || 'Main Org')
      : (current.title || 'Untitled Team')
    const locationAddress = getTeamLocationAddress(current)

    return (
      <div className={css(Styles.nameCell)}>
        <div className={css(Styles.nameRow)}>
          <span className={css(Styles.line1, Styles.nameTitle)}>{displayName}</span>
          {isMain && (
            <span className={css(Styles.mainOrgChip)}>Main Org</span>
          )}
        </div>
        {locationAddress && (
          <span className={css(Styles.locationAddress)}>
            <i className={`material-icons-outlined ${css(Styles.locationIcon)}`}>place</i>
            <span>{locationAddress}</span>
          </span>
        )}
      </div>
    )
  }

  renderInboundRoutingCell(current) {
    const isMain = isMainOrgTeam(current, this.parentOrgId)
    if (isMain) {
      return <span className={css(Styles.line2)}>—</span>
    }
    const chips = formatInboundRoutingSummary(current)
    if (chips.length === 0) {
      return <span className={css(Styles.line2)}>—</span>
    }
    return (
      <div className={css(Styles.chipsContainer)}>
        {chips.map((chip, idx) => (
          <Tooltip title={chip.tooltip} key={idx} placement='top-start'>
            <span
              className={css(
                Styles.tagChip,
                chip.type === 'slugs'
                  ? Styles.slugChip
                  : chip.type === 'metaForms'
                  ? Styles.metaChip
                  : Styles.numberChip
              )}
            >
              {chip.label}
            </span>
          </Tooltip>
        ))}
      </div>
    )
  }

  renderOutboundCell(current) {
    const isMain = isMainOrgTeam(current, this.parentOrgId)
    if (isMain) {
      return <span className={css(Styles.line2)}>—</span>
    }
    const chips = formatOutboundSummary(current, this.state.fieldDefOutbound)
    if (chips.length === 0) {
      return <span className={css(Styles.line2)}>—</span>
    }
    return (
      <div className={css(Styles.chipsContainer)}>
        {chips.map((chip, idx) => (
          <Tooltip title={chip.tooltip} key={idx} placement='top-start'>
            <span
              className={css(
                Styles.tagChip,
                chip.type === 'wa' ? Styles.waChip : Styles.ctcChip
              )}
            >
              {chip.label}
            </span>
          </Tooltip>
        ))}
      </div>
    )
  }

  onActions = (current) => {
    const actions = []
    actions.push(
      <Tooltip title='View Staff' placement='top-start' key={`${current.id}-staff`}>
        <span className={css(AppStyles.entityTableLinkIcon)}>
          <i
            className='material-icons-outlined'
            onClick={(e) => {
              e.stopPropagation()
              this.openStaffDrawer(current)
            }}
          >
            people
          </i>
        </span>
      </Tooltip>
    )
    actions.push(
      <Tooltip title='Edit Details & Fields' placement='top-start' key={`${current.id}-edit`}>
        <span className={css(AppStyles.entityTableLinkIcon)}>
          <i
            className='material-icons-outlined'
            onClick={(e) => {
              e.stopPropagation()
              this.openEditDialog(current)
            }}
          >
            edit
          </i>
        </span>
      </Tooltip>
    )
    return actions
  }

  onMenuItems = (current) => {
    const isMain = isMainOrgTeam(current, this.parentOrgId)
    const menuItems = [
      {
        current,
        key: `${current.id}_staff`,
        action: () => this.openStaffDrawer(current),
        title: 'View Staff',
        icon: 'people',
      },
      {
        current,
        key: `${current.id}_edit`,
        action: () => this.openEditDialog(current),
        title: 'Edit Details & Fields',
        icon: 'edit',
      },
    ]

    if (!isMain) {
      menuItems.push(
        {
          current,
          key: `${current.id}_routing`,
          action: () => this.openRoutingDialog(current),
          title: 'Inbound Routing',
          icon: 'call_received',
        },
        {
          current,
          key: `${current.id}_outbound`,
          action: () => this.openOutboundDialog(current),
          title: 'Outbound Configuration',
          icon: 'call_made',
        }
      )
    }

    const actions = []
    menuItems.forEach((item) => {
      actions.push(UiHelper.actionButton(item))
    })
    return actions
  }

  onTableValue(current, index) {
    if (!this.tableHeaders[index]) return ''
    return this.tableHeadersMap[this.tableHeaders[index]](current)
  }

  renderAboveTable() {
    return (
      <SearchBar
        hint='Search by team title, slug, or address'
        onSearch={this.onSearch}
        onClearSearch={this.clearSearch}
        searchText={this.state.searchText}
        searchButtonLabel='Filter'
        clearSearchLabel='Clear'
        autoRefresh
        debounceDuration={1}
        autoFocus={false}
        hideFiltersContainer
      />
    )
  }

  renderBelowTable() {
    return (
      <TeamStaffDrawer
        open={this.state.staffDrawerOpen}
        onClose={this.closeStaffDrawer}
        team={this.state.selectedTeam}
        parentOrgId={this.parentOrgId}
      />
    )
  }
}

const Styles = StyleSheet.create({
  cellWidthName: {
    width: '34%',
    minWidth: 220,
  },
  cellWidthInbound: {
    width: '30%',
    minWidth: 200,
  },
  cellWidthOutbound: {
    width: '26%',
    minWidth: 180,
  },
  cellWidthActions: {
    width: '10%',
    minWidth: 80,
    textAlign: 'right',
  },
  line1: {
    fontSize: 16,
  },
  line2: {
    fontSize: 13,
    color: 'gray',
    display: 'block',
    marginTop: 2,
  },
  locationAddress: {
    fontSize: 13,
    color: '#4B5563',
    display: 'flex',
    alignItems: 'flex-start',
    marginTop: 2,
    lineHeight: '18px',
    wordBreak: 'break-word',
  },
  locationIcon: {
    fontSize: 14,
    marginRight: 4,
    marginTop: 2,
    color: '#6B7280',
    flexShrink: 0,
  },
  nameCell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  nameRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  nameTitle: {
    marginRight: 6,
  },
  mainOrgChip: {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    padding: '2px 8px',
    backgroundColor: '#EDE7F6',
    color: '#5E35B1',
    letterSpacing: 0.2,
  },
  chipsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
  },
  tagChip: {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 500,
    padding: '2px 8px',
    lineHeight: '18px',
    cursor: 'default',
  },
  slugChip: {
    backgroundColor: '#E0F2FE',
    color: '#0369A1',
  },
  metaChip: {
    backgroundColor: '#F0FDF4',
    color: '#15803D',
  },
  numberChip: {
    backgroundColor: '#FEF3C7',
    color: '#B45309',
  },
  waChip: {
    backgroundColor: '#DCFCE7',
    color: '#166534',
  },
  ctcChip: {
    backgroundColor: '#E0E7FF',
    color: '#3730A3',
  },
})
